import { Preferences } from "@capacitor/preferences";
import { debounce } from "lodash";
import { useContext, useEffect, useState } from "react";
import {
  Link,
  LoaderFunction,
  useLoaderData,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import { get } from "api";
import { Button } from "atoms/button/Button";
import { Icon } from "atoms/icon/Icon";
import ChevronLeft from "atoms/icon/icons/chevron_left.svg";
import MapPin from "atoms/icon/icons/map_pin.svg";
import QrCode from "atoms/icon/icons/qr_code.svg";
import { IconButton } from "atoms/icon-button/IconButton";
import { Spinner } from "atoms/Spinner";
import { useCreateMultiPlatformUser } from "hooks/useCreateMultiPlatformUser";
import { QRScanner } from "organisms/qr-scanner/QRScanner";
import { queryClient, ThemeContext } from "pages/Root";
import { getFeatureFlag } from "services/Flags";
import { waitObj } from "toolbox/Promise";

const load = async () => {
  const user = await queryClient
    .fetchQuery({
      queryKey: ["/user"],
      queryFn: () => get("/user"),
      staleTime: 1000 * 60 * 2,
    })
    .catch(() => null);

  const multiPlatformVerifyToken = Preferences.get({
    key: "multiPlatformVerifyToken",
  });

  return waitObj({ multiPlatformVerifyToken, user });
};

type LoaderData = Awaited<ReturnType<typeof load>>;
export const findMedspaLoader: LoaderFunction = () => {
  return load();
};

const getPlatformIdFromScannedCodeDebounced = debounce(
  (scannedCode: string) =>
    get("/multi-platform/platform/qrcode", { scannedCode }),
  500,
  { leading: true, trailing: false }
);

export const FindMedspaPage = () => {
  const { multiPlatformVerifyToken, user } = useLoaderData() as LoaderData;
  const { resetTheme } = useContext(ThemeContext);
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [scanBeingProcessed, setScanBeingProcessed] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [searchParams] = useSearchParams();
  const createMultiPlatformUserMutation = useCreateMultiPlatformUser();
  const referralCode = searchParams.get("referralCode");
  const navigate = useNavigate();

  const handleScanError = (error: any) => {
    const errorStatus = error.response?.status;
    if (errorStatus == 400 || errorStatus == 401) {
      setErrorMessage("Unrecognized QR code - please try again");
    } else {
      setErrorMessage(
        "Something went wrong - please contact support if this issue persists"
      );
    }
    setShowErrorMessage(true);
  };

  const redirectToCompleteProfile = async (
    platformId: string,
    platformLocationId: string
  ) => {
    const platformTheme = await get(
      "/multi-platform/platform/{platformId}/theme",
      { platformId }
    );

    const referralCodeParam = referralCode
      ? `&referralCode=${referralCode}`
      : "";

    const baseUrl = "/signin/location";
    const redirectUrl = `${baseUrl}?platformId=${platformId}&platformImageUrl=${platformTheme.logoImageUrl}&locationId=${platformLocationId}${referralCodeParam}`;

    resetTheme();
    navigate(redirectUrl);
  };

  const createOrRedirectUser = debounce(
    (platformId: string, platformLocationId: string) => {
      if (!!multiPlatformVerifyToken?.value || user?.isGuest === false) {
        return redirectToCompleteProfile(platformId, platformLocationId);
      }
      if (
        createMultiPlatformUserMutation.isLoading ||
        createMultiPlatformUserMutation.isSuccess
      ) {
        return;
      }
      createMultiPlatformUserMutation.mutate({
        platformLocationId,
        platformId,
        userId: user?.id,
      });
    },
    500
  );

  const handleScannedCode = async (scannedCode: string) => {
    if (scanBeingProcessed) {
      return;
    }

    try {
      const { platformId, platformLocationId } =
        await getPlatformIdFromScannedCodeDebounced(scannedCode);
      if (!platformId || !platformLocationId) {
        return;
      }
      setScanBeingProcessed(true);
      setShowQRScanner(false);
      createOrRedirectUser(platformId, platformLocationId);
    } catch (error: any) {
      setScanBeingProcessed(false);
      handleScanError(error);
    }
  };

  useEffect(() => {
    if (showErrorMessage) {
      const timeout = setTimeout(() => {
        setShowErrorMessage(false);
        setErrorMessage("");
      }, 2000);

      return () => clearTimeout(timeout);
    }
  }, [navigate, showErrorMessage]);

  return (
    <>
      {showQRScanner && (
        <QRScanner
          errorMessage={showErrorMessage ? errorMessage : undefined}
          onClose={() => {
            setShowQRScanner(false);
            setShowErrorMessage(false);
            setErrorMessage("");
          }}
          onResult={handleScannedCode}
        />
      )}

      {(createMultiPlatformUserMutation.isLoading ||
        createMultiPlatformUserMutation.isSuccess) && (
        <div className="mt-10 flex flex-col items-center justify-center gap-6">
          <Spinner />
        </div>
      )}

      <div className="flex h-screen flex-col bg-one px-4 pb-[calc(20px_+_env(safe-area-inset-bottom))] pt-[calc(20px_+_env(safe-area-inset-top))]">
        <Link to="/signin/verification">
          <IconButton size="small" style="iconOnly" svg={ChevronLeft} />
        </Link>
        <div className="flex flex-1 flex-col justify-between">
          <div>
            <div className="mb-8 flex flex-col items-center">
              <Icon
                size="large"
                svg={MapPin}
                color="brandColor"
                className="mb-11"
              />
              <div className="mb-3 font-serif text-h1">Find your location</div>
            </div>

            <div className="mx-4 mb-8 text-center text-body1 text-secondary">
              Find your location now to shop your favorite services, and start
              earning rewards with every purchase and visit!
            </div>
          </div>
          <div className="flex flex-col gap-4">
            <Button
              size="large"
              fullWidth
              onClick={() => setShowQRScanner(true)}
            >
              <div className="flex items-center gap-1.5">
                <Icon size="xsmall" color="white" svg={QrCode} />
                Scan QR code in location
              </div>
            </Button>

            <Button
              size="large"
              style="secondary"
              fullWidth
              onClick={async () => {
                if (referralCode) {
                  localStorage.setItem("referralCode", referralCode);
                }
                navigate(`/signin/platform-picker`);
              }}
            >
              <div className="flex items-center gap-1.5">
                <Icon size="xsmall" color="brandColor" svg={MapPin} />
                Search by location
              </div>
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};
